import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Users, TrendingUp, Palette, MapPin, CalendarCheck, BadgeCheck, Anchor } from "lucide-react";

const Metric = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-white rounded-2xl border border-border p-5" data-testid={testid}>
    <div className="flex items-center gap-2 mb-2 text-muted-foreground">
      <Icon className="w-4 h-4" />
      <span className="text-xs uppercase tracking-widest font-semibold">{label}</span>
    </div>
    <div className="font-serif text-2xl font-bold text-navy">{value ?? "Not enough data yet"}</div>
  </div>
);

export default function PersonaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/intelligence/personas/${id}/detail`).then(({ data }) => setData(data))
      .catch(() => setData(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="text-center py-16 text-muted-foreground">Loading...</div>;
  if (!data) return <div className="text-center py-16 text-muted-foreground">Persona not found.</div>;

  const p = data.persona || {};

  return (
    <div className="space-y-8 fade-up" data-testid="persona-detail-page">
      <Button variant="ghost" onClick={() => navigate("/buyer-intelligence")} className="text-gold -ml-2" data-testid="persona-back-btn">
        <ArrowLeft className="w-4 h-4 mr-1" /> Buyer Intelligence
      </Button>

      <div className="relative overflow-hidden rounded-3xl navy-gradient p-8">
        <div className="relative z-10">
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-2" data-testid="persona-name">{p.name}</h1>
          <p className="text-slate-300 max-w-2xl">{p.description}</p>
          <div className="flex flex-wrap gap-1.5 mt-4">
            {(p.typical_categories || []).map((c) => (
              <span key={c} className="text-[11px] font-semibold text-navy bg-gold/90 px-2.5 py-0.5 rounded-full">{c}</span>
            ))}
          </div>
        </div>
      </div>

      {data.not_enough_data ? (
        <div className="bg-white rounded-2xl border border-dashed border-gold/50 p-10 text-center" data-testid="persona-not-enough-data">
          <Anchor className="w-9 h-9 mx-auto mb-3 text-gold/70" />
          <h2 className="font-serif text-lg font-bold text-navy mb-1">Not enough data yet</h2>
          <p className="text-muted-foreground max-w-lg mx-auto">
            We have {data.matched_signals ?? 0} matching buyer signal{data.matched_signals === 1 ? "" : "s"} for this persona
            (need at least {data.min_signals ?? 3}). Keep marketing this inventory to build a real picture.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <Metric icon={Users} label="Active Prospects" value={data.active_prospects} testid="persona-prospects" />
            <Metric icon={TrendingUp} label="Average Intent" value={data.average_intent != null ? `${data.average_intent}/100` : null} testid="persona-intent" />
            <Metric icon={CalendarCheck} label="Appointments" value={data.appointments} testid="persona-appointments" />
            <Metric icon={BadgeCheck} label="Sales" value={data.sales} testid="persona-sales" />
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <Metric icon={Anchor} label="Most Viewed Inventory" value={(data.most_viewed_inventory || []).join(", ") || null} testid="persona-inventory" />
            <Metric icon={Palette} label="Top Creative Theme" value={data.top_creative_theme_label} testid="persona-theme" />
            <Metric icon={MapPin} label="Top Channel" value={data.top_channel} testid="persona-channel" />
          </div>
        </>
      )}

      <section className="bg-white rounded-2xl border border-border p-6" data-testid="persona-profile">
        <h2 className="font-serif text-xl font-bold text-navy mb-4">Persona Profile</h2>
        <div className="grid gap-5 sm:grid-cols-2">
          <ProfileList title="Likely motivations" items={p.motivations} />
          <ProfileList title="Likely objections" items={p.objections} />
          <ProfileList title="Preferred messaging themes" items={(p.messaging_themes || [])} />
          <ProfileList title="Recommended channels" items={p.channels} />
          <ProfileList title="CTA preferences" items={p.cta_preferences} />
          <ProfileList title="Inventory characteristics" items={p.inventory_characteristics} />
        </div>
        {p.geo_considerations && (
          <div className="mt-5 text-sm text-muted-foreground"><span className="font-semibold text-navy">Geographic considerations:</span> {p.geo_considerations}</div>
        )}
      </section>
    </div>
  );
}

const ProfileList = ({ title, items }) => (
  <div>
    <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">{title}</div>
    {items && items.length ? (
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-sm text-navy flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-gold mt-1.5 shrink-0" />{it}
          </li>
        ))}
      </ul>
    ) : (
      <div className="text-sm text-muted-foreground">Not set</div>
    )}
  </div>
);
